import { apiSlice } from './apiSlice'

export interface RubricCriterion {
  key: string
  name: string
  description?: string
  max_score: number
  weight?: number
}

export interface Rubric {
  id?: string
  name: string
  description?: string
  criteria: RubricCriterion[]
  case_id?: string
  created_at?: string
  updated_at?: string
  created_by?: string
}

export interface RubricCreate {
  name: string
  description?: string
  criteria: RubricCriterion[]
  case_id?: string
}

export interface Score {
  id: string
  memorial_id?: string
  transcript_id?: string
  submission_type?: string // "memorial" or "transcript"
  case_id: string
  team_id: string
  judge_id: string
  rubric_id?: string
  rubric_scores: Record<string, number>
  total_score: number
  weighted_score?: number
  comments?: string
  ai_suggestions?: Record<string, any>
  /** draft = chỉnh sửa được; submitted = đã gửi chính thức */
  status?: string | null
  submitted_at?: string | null
  scored_at?: string
  created_at?: string
  updated_at?: string
}

export interface ScoreCreate {
  memorial_id?: string
  transcript_id?: string
  submission_type?: string // "memorial" or "transcript"
  case_id: string
  team_id: string
  rubric_id?: string
  rubric_scores: Record<string, number>
  comments?: string
  ai_suggestions?: Record<string, any> | null
}

export interface ScoreUpdate {
  rubric_scores?: Record<string, number>
  comments?: string
  ai_suggestions?: Record<string, any>
}

export interface AISuggestions {
  suggested_scores: Record<string, number>
  analysis: string
  recommendations: string[]
  confidence?: number
}

export const scoringApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getScores: builder.query<Score[], { case_id?: string; team_id?: string; judge_id?: string }>({
      query: (params) => ({
        url: '/scoring/list',
        params,
      }),
      providesTags: ['Score'],
    }),
    getScore: builder.query<Score, string>({
      query: (id) => `/scoring/${id}`,
      providesTags: (result, error, id) => [{ type: 'Score', id }],
    }),
    createScore: builder.mutation<Score, ScoreCreate>({
      query: (score) => ({
        url: '/scoring/create',
        method: 'POST',
        body: score,
      }),
      invalidatesTags: ['Score'],
    }),
    updateScore: builder.mutation<Score, { id: string; update: ScoreUpdate }>({
      query: ({ id, update }) => ({
        url: `/scoring/${id}`,
        method: 'PUT',
        body: update,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Score', id }, 'Score'],
    }),
    deleteScore: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/scoring/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Score'],
    }),
    submitScore: builder.mutation<Score, string>({
      query: (id) => ({
        url: `/scoring/${id}/submit`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Score', id }, 'Score'],
    }),
    unlockScore: builder.mutation<Score, string>({
      query: (id) => ({
        url: `/scoring/${id}/unlock`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Score', id }, 'Score'],
    }),
    getAISuggestions: builder.query<AISuggestions, string>({
      query: (memorialId) => `/scoring/ai-suggestions/${memorialId}`,
    }),
    runAiPreliminaryMemorial: builder.mutation<
      {
        memorial_id: string
        case_id: string
        team_id: string
        excerpts: Record<string, unknown>[]
        governance_notice: string
      },
      string
    >({
      query: (memorialId) => ({
        url: `/scoring/ai-preliminary/memorial/${memorialId}`,
        method: 'POST',
      }),
    }),
    // Rubric endpoints
    getRubrics: builder.query<Rubric[], { case_id?: string }>({
      query: (params) => ({
        url: '/scoring/rubrics',
        params,
      }),
      providesTags: ['Rubric'],
    }),
    getRubric: builder.query<Rubric, string>({
      query: (id) => `/scoring/rubrics/${id}`,
      providesTags: (result, error, id) => [{ type: 'Rubric', id }],
    }),
    createRubric: builder.mutation<Rubric, RubricCreate>({
      query: (rubric) => ({
        url: '/scoring/rubrics',
        method: 'POST',
        body: rubric,
      }),
      invalidatesTags: ['Rubric'],
    }),
    updateRubric: builder.mutation<Rubric, { id: string; update: Partial<RubricCreate> }>({
      query: ({ id, update }) => ({
        url: `/scoring/rubrics/${id}`,
        method: 'PUT',
        body: update,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Rubric', id }, 'Rubric'],
    }),
    deleteRubric: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/scoring/rubrics/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Rubric'],
    }),
  }),
})

export const {
  useGetScoresQuery,
  useGetScoreQuery,
  useCreateScoreMutation,
  useUpdateScoreMutation,
  useDeleteScoreMutation,
  useSubmitScoreMutation,
  useUnlockScoreMutation,
  useLazyGetAISuggestionsQuery,
  useRunAiPreliminaryMemorialMutation,
  useGetRubricsQuery,
  useGetRubricQuery,
  useCreateRubricMutation,
  useUpdateRubricMutation,
  useDeleteRubricMutation,
} = scoringApi
